/* global gtag */
/**
 * Helper functions for the entire web application.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {Highlight} from "prism-react-renderer";

/**
 * Format a start or end time of a dataset for display in the UI.
 *
 * @param time (in ISO8601 format)
 * @return human readable string.
 */
export function formatStartOrEndTime(time) {
    return time.split("T")[0];
}


/**
 * Format a duration unit with the correct singular or plural form.
 *
 * Replaces occurrences of a duration component (for example, "1Y" or "2D")
 * with a human-readable string such as "1 year ago" or "2 days ago".
 *
 * @param str String containing one or more duration components.
 * @param letter Duration designator (Y, M, D, H, or S).
 * @param unit Human-readable name of the duration unit.
 * @return String with the specified duration component formatted for display.
 */
function formatUnit(str, letter, unit) {
    return str.replace(new RegExp(`(\\d+)${letter}`, "g"), (_, value) => {
        // Convert the string (e.g., "01") to a number (1)
        const numValue = parseInt(value, 10);
        return `${numValue} ${unit}${numValue === 1 ? "" : "s"} ago`;
    });
}

/**
 * Format a relative end time of a dataset for display in the UI.
 *
 * @param relativeEnd (relative end time, like "-P1Y")
 * @return human readable string.
 */
export function formatRelativeEnd(relativeEnd) {
    var formattedString = relativeEnd;

    if (typeof formattedString !== "string") {
        return "Invalid date";
    }

    if (formattedString.includes("T")) {

        let [datePart, timePart] = formattedString.split("T");
        datePart = datePart.replace("-P", "").replace("P", "");

        if (datePart) {
            formattedString = datePart;
            formattedString = formatUnit(formattedString, "Y", "year");
            formattedString = formatUnit(formattedString, "M", "month");
            formattedString = formatUnit(formattedString, "D", "day");
        } else {
            formattedString = timePart;
            formattedString = formatUnit(formattedString, "H", "hour");
            formattedString = formatUnit(formattedString, "M", "minute");
            formattedString = formatUnit(formattedString, "S", "second");
        }

    } else {
        formattedString = formattedString
            .replace("-P", "")
            .replace("P", "");

        formattedString = formatUnit(formattedString, "Y", "year");
        formattedString = formatUnit(formattedString, "M", "month");
        formattedString = formatUnit(formattedString, "D", "day");
    }

    if (formattedString.startsWith("0")) {
        // Will only replace first instance
        formattedString = formattedString.replace("0", "");
    }
    return "About " + formattedString;
}

/**
 * Helper function for scrolling to the top of the page, which
 * can be used as a JSX element. Used in <Header />.
 */
export function ScrollToTop() {
  // Extracts pathname property(key) from an object
  const { pathname } = useLocation();

  // Automatically scrolls to top whenever pathname changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
}


export function HilightedCode(props) {
    const {style, code, language, theme}  = props;
    let styleInner = {...style, maxWidth: "100%" };
    return (
          <div style={style}>
            <Highlight code={code} language={language} theme={theme}>
              {({ className, style, tokens, getLineProps, getTokenProps }) => (
                  <pre style={{...style, overflowX: "auto", padding: "20px", marginBottom: "20px", marginTop: "0px"}}>
                    {tokens.map((line, i) => (
                        <div key={i} {...getLineProps({ line })}>
                          {line.map((token, key) => (
                              <span key={key} {...getTokenProps({ token })} />
                          ))}
                        </div>
                    ))}
                  </pre>
              )}
            </Highlight>
          </div>
    );
}


/**
 * Function that converts nominal time to UTC.
 *
 * @param String whose value is a nominal time.
 * @return Datetime string.
 */
export function convertNominalTimeToUTC(nominalTime) {
    const currentDate = new Date();
    const matches = nominalTime.match(/P(\d+)([YMD])/);

    if (matches && matches.length === 3) {
        const value = parseInt(matches[1], 10);
        const unit = matches[2];
        const targetDate = new Date(Date.UTC(
            currentDate.getUTCFullYear() - (unit === 'Y' ? value : 0),
            currentDate.getUTCMonth() - (unit === 'M' ? value : 0),
            currentDate.getUTCDate() - (unit === 'D' ? value : 0),
            currentDate.getUTCHours(),
            currentDate.getUTCMinutes(),
            currentDate.getUTCSeconds(),
            currentDate.getUTCMilliseconds()
        ));

        const year = targetDate.getUTCFullYear();
        const month = String(targetDate.getUTCMonth() + 1).padStart(2, '0');
        const day = String(targetDate.getUTCDate()).padStart(2, '0');
        const hours = String(targetDate.getUTCHours()).padStart(2, '0');
        const minutes = String(targetDate.getUTCMinutes()).padStart(2, '0');
        const seconds = String(targetDate.getUTCSeconds()).padStart(2, '0');

        const isoString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
        return isoString;
    } else {
        return null;
    }
}


/**
 * Formats a given UTC date object into a string in the format `YYYY-MM-DD HH:MM:SS`.
 * The format starts with the year, followed by the month, day, and time in 24-hour format.
 *
 * @param utcDate Date A valid JavaScript Date object representing a date in UTC.
 * @returns String The formatted date string in the format `YYYY-MM-DD HH:MM:SS`.
 */
export function yearFirstDatetimeFormat(date, utc = false)
{
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}    ${hours}:${minutes}:${seconds}`;
}


/**
 * Sends a custom event to Google Analytics 4 (GA4) when a button is clicked.
 *
 * This function checks if the Google Analytics `gtag` function is available and
 * then sends an event with the specified label. If GA4 is not loaded, an error
 * is logged to the console.
 *
 * @param label String The label for the event, typically identifying the
 * specific button or action being tracked.
 *
 * @param eventType String type of event.
 * @param eventValue String label for event type.
 *
 * @return void
 */
export function sendEventToGA(label, eventType, eventValue) {
    if (typeof gtag !== 'undefined') {
       const eventObj = {
       event_category: 'user_interaction',
       event_label: `${label}`,
       value: 1,
       };

       if(eventType) {
           eventObj[`${eventType}`] = eventValue;
       }

       gtag('event', 'button_click', eventObj);
    } else {
       console.error('Google Analytics not loaded');
    }
}


const MAX_CITATION_AUTHORS = 5;

/**
 * Shorten a SPASE Authors string for the readable citation. Lists of more
 * than MAX_CITATION_AUTHORS names keep the first five and end in "et al.";
 * anything shorter is returned exactly as given.
 *
 * SPASE author strings are free text, so the parser is a heuristic
 * (see .claude/docs/citation-author-formats.md for the shapes it handles).
 *
 * @param authors Raw Authors string from the dataset record (may be null)
 * @return Author string to show in the citation
 */
export function formatCitationAuthors(authors) {
    if (!authors) {
        return authors;
    }

    const semicolonStyle = authors.includes(";");
    const names = semicolonStyle
          ? splitSemicolonAuthors(authors)
          : splitCommaAuthors(authors);

    if (names.length <= MAX_CITATION_AUTHORS) {
        return authors;
    }

    const separator = semicolonStyle ? "; " : ", ";
    return names.slice(0, MAX_CITATION_AUTHORS).join(separator) + separator + "et al.";
}

const ET_AL = /^et al\.?$/i;
const LEADING_AND = /^(?:and|&)\s+/i;

// "Last, First; Last, First; ..." (SPASE convention). Drops empty parts,
// a leading "and"/"&" on a name, and a trailing "et al." part.
function splitSemicolonAuthors(authors) {
    return authors
        .split(";")
        .map((part) => part.trim().replace(LEADING_AND, ""))
        .filter((part) => part && !ET_AL.test(part));
}

const INITIALS = /^(?:[A-Z]\.?\s*)+$/;                 // "R.E.", "S.", "A. K."
const INITIALS_FIRST = /^(?:[A-Z]\.\s*)+\S/;           // "J.D. Patterson"
const NAME_SUFFIX = /^(?:Jr|Sr|II|III|IV)\.?$/i;
const TRAILING_ET_AL = /,?\s*et al\.?\s*$/i;
const LAST_AUTHOR = /^(.*?),?\s+(?:and|&)\s+(.+)$/;

// Comma-only lists: "Last, I., Last, I., and Last, I." or "I. Last, I. Last".
// The name after the final "and"/"&" is one author. The rest is split on
// commas and regrouped: a token that is not initials or a suffix starts a
// new author when it is written initials-first, or when the previous token
// finished a name (initials, or an initials-first name).
function splitCommaAuthors(authors) {
    let head = authors.replace(TRAILING_ET_AL, "").trim();
    let lastAuthor = null;

    const match = head.match(LAST_AUTHOR);
    if (match) {
        head = match[1];
        lastAuthor = match[2].trim();
    }

    const names = [];
    let previousFinishedName = false;

    for (const token of head.split(",").map((t) => t.trim()).filter(Boolean)) {
        const isInitials = INITIALS.test(token);
        const isInitialsFirst = !isInitials && INITIALS_FIRST.test(token);
        const isSuffix = NAME_SUFFIX.test(token);
        const startsName = !isInitials && !isSuffix
              && (isInitialsFirst || previousFinishedName);

        if (names.length === 0 || startsName) {
            names.push(token);
        } else {
            names[names.length - 1] += ", " + token;
        }
        previousFinishedName = isInitials || isInitialsFirst;
    }

    if (lastAuthor) {
        names.push(lastAuthor);
    }
    return names;
}
